import { useCallback, useEffect, useRef, useState } from "react";
import { Cube, Warning, Sparkle, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fmtErr } from "../lib/api";
import { useProject } from "../context/ProjectContext";
import DesignViewer from "../components/viewer3d/DesignViewer";
import SpecRail from "../components/viewer3d/SpecRail";
import InputsPanel from "../components/viewer3d/InputsPanel";
import CompliancePanel from "../components/viewer3d/CompliancePanel";
import ExportsPanel from "../components/viewer3d/ExportsPanel";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const SEV = { spec_issue: "text-[var(--c-red)]", advisory: "text-[var(--c-amber)]", info: "text-[var(--c-muted)]" };

/** Model Engine — Phase 1 viewer core + Phase 2 generation from the confirmed spec. */
export default function ModelEngine() {
  const { projects, pid, setPid } = useProject();
  const [mode, setMode] = useState("project");
  const [models, setModels] = useState([]);
  const [modelId, setModelId] = useState("t401a");
  const [model, setModel] = useState(null);
  const [inputs, setInputs] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [rail, setRail] = useState("spec");
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showInputs, setShowInputs] = useState(true);
  const viewerRef = useRef(null);

  useEffect(() => {
    api.get("/engine/design-models").then(({ data }) => setModels(data.models || [])).catch(() => setModels([]));
  }, []);

  useEffect(() => {
    if (mode !== "reference") return;
    setModel(null); setSelected(null);
    api.get(`/engine/design-model/${modelId}`).then(({ data }) => setModel(data)).catch((e) => toast.error(fmtErr(e)));
  }, [modelId, mode]);

  useEffect(() => {
    if (mode !== "project" || !pid) return undefined;
    let live = true;
    setModel(null); setInputs(null); setSelected(null); setCompliance(null);
    api.get(`/engine/${pid}/inputs`).then(({ data }) => { if (live) setInputs(data); })
      .catch(() => { if (live) setInputs(null); });
    api.get(`/engine/${pid}/design-model`)
      .then(({ data }) => { if (live) { setModel(data); setShowInputs(false); } })
      .catch(() => { if (live) { setModel(null); setShowInputs(true); } });
    api.get(`/engine/${pid}/validation`).then(({ data }) => { if (live) setCompliance(data); })
      .catch(() => { if (live) setCompliance(null); });
    return () => { live = false; };
  }, [pid, mode]);

  const runValidation = useCallback(async () => {
    if (!pid) return;
    try {
      setBusy(true);
      const { data } = await api.post(`/engine/${pid}/validate`);
      setCompliance(data);
      setRail("compliance");
      const blocked = data.status === "BLOCKED";
      (blocked ? toast.error : toast.success)(
        `${data.counts.PASS} passed · ${data.counts.FAIL} failed · ${data.counts["SPEC ISSUE"]} spec issue(s)`);
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  }, [pid]);

  const seedDemo = async () => {
    try {
      const { data } = await api.post("/engine/demo-project");
      toast.success(`${data.project_code} ready — ${data.created ? "created" : "refreshed"}`);
      setPid(data.project_id);
      window.location.reload();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const pick = useCallback((id) => {
    setSelected((cur) => (cur === id ? null : id));
    viewerRef.current?.select(id);
  }, []);

  const meta = model?.meta || {};

  return (
    <div className="flex h-full flex-col" data-testid="model-engine-page">
      <div className="flex flex-wrap items-center gap-3 border-b border-[var(--c-border)] bg-[var(--c-surface)] px-4 py-2.5">
        <div className="flex items-center gap-2">
          <Cube size={17} className="text-[var(--c-accent)]" />
          <span className="text-sm font-medium text-[var(--c-text)]">Model Engine</span>
        </div>
        <div className="flex rounded-sm border border-[var(--c-border)] p-[2px]">
          {[["project", "From spec"], ["reference", "Reference"]].map(([k, l]) => (
            <button key={k} data-testid={`me-mode-${k}`} onClick={() => setMode(k)}
              className={`h-6 rounded-sm px-2.5 text-[11px] ${mode === k ? "bg-[var(--c-accent)] text-white" : "text-[var(--c-muted)] hover:text-[var(--c-text)]"}`}>
              {l}
            </button>
          ))}
        </div>
        {mode === "reference" ? (
          <Select value={modelId} onValueChange={setModelId}>
            <SelectTrigger data-testid="me-model-select"
              className="h-8 w-80 rounded-sm border-[var(--c-border)] bg-[var(--c-bg)] text-xs text-[var(--c-text)]">
              <SelectValue placeholder="Select a design model…" />
            </SelectTrigger>
            <SelectContent className="border-[var(--c-border)] bg-[var(--c-surface)]">
              {models.map((m) => (
                <SelectItem key={m.id} value={m.id} data-testid={`me-model-option-${m.id}`}
                  className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">{`${m.tag} — ${m.title}`}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <>
            <Select value={pid} onValueChange={setPid}>
              <SelectTrigger data-testid="me-project-select"
                className="h-8 w-80 rounded-sm border-[var(--c-border)] bg-[var(--c-bg)] text-xs text-[var(--c-text)]">
                <SelectValue placeholder="Select project…" />
              </SelectTrigger>
              <SelectContent className="max-h-80 border-[var(--c-border)] bg-[var(--c-surface)]">
                {(projects || []).map((p) => (
                  <SelectItem key={p.id} value={p.id} data-testid={`me-project-option-${p.id.slice(0, 8)}`}
                    className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">
                    {`${p.project_code} — ${(p.product_summary || "—").slice(0, 40)}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button data-testid="me-seed-demo" onClick={seedDemo}
              className="h-7 rounded-sm border border-[var(--c-border)] px-2.5 text-[11px] text-[var(--c-muted)] hover:text-[var(--c-text)]">
              T-401A demo project
            </button>
            {inputs && (
              <button data-testid="me-toggle-inputs" onClick={() => setShowInputs((v) => !v)}
                className="h-7 rounded-sm border border-[var(--c-border)] px-2.5 text-[11px] text-[var(--c-text)] hover:border-[var(--c-accent)]">
                {showInputs ? "Hide inputs" : "Design inputs"}
              </button>
            )}
          </>
        )}
        <span className="font-mono text-[10px] text-[var(--c-muted)]" data-testid="me-schema-badge">
          {model?.schema || "formigen.design/1.0"} · {mode === "project" ? "Phase 4 · rules, checks + exports" : "Phase 1 · viewer core"}
        </span>
        {mode === "project" && model && (
          <button data-testid="me-validate-btn" onClick={runValidation} disabled={busy}
            className="flex h-7 items-center gap-1.5 rounded-sm border border-[var(--c-border)] px-2.5 text-[11px] text-[var(--c-text)] hover:border-[var(--c-accent)] disabled:opacity-40">
            <ShieldCheck size={12} /> {compliance ? `Checks · ${compliance.status}` : "Run checks"}
          </button>
        )}
        {model && !meta.sme_approved && (
          <span data-testid="me-sme-warning"
            className="ml-auto flex items-center gap-1.5 rounded-sm border border-[var(--c-amber)]/40 bg-[var(--c-amber)]/10 px-2 py-1 font-mono text-[10px] text-[var(--c-amber)]">
            <Warning size={12} /> rule tables not SME approved
          </span>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="grid min-h-0 grid-cols-1 gap-4 p-4 lg:grid-cols-[minmax(0,1fr)_420px]">
          <div className="flex min-w-0 flex-col gap-3">
            {model ? (
              <DesignViewer ref={viewerRef} model={model} onSelect={setSelected} className="h-[70vh] min-h-[26rem]" />
            ) : (
              <div className="grid h-[70vh] place-items-center rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)] p-6 text-center"
                data-testid="me-empty">
                <div className="max-w-md">
                  <Sparkle size={22} className="mx-auto mb-2 text-[var(--c-accent)]" />
                  <p className="text-sm text-[var(--c-text)]">No design model for this project yet</p>
                  <p className="mt-1 text-xs text-[var(--c-muted)]">
                    Confirm the design inputs on the right, then hit <b>Confirm &amp; generate</b> — the
                    transformer rule pack derives every dimension and position from the spec.
                  </p>
                </div>
              </div>
            )}

            {!!model?.deviations?.length && (
              <div className="overflow-hidden rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)]" data-testid="me-deviations">
                <div className="border-b border-[var(--c-border)] px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-[var(--c-muted)]">
                  Deviations — the stated value is built, the calculation is reported
                </div>
                <table className="w-full text-[12px]">
                  <tbody>
                    {model.deviations.map((d, i) => (
                      <tr key={i} className="border-b border-[var(--c-border)]/50" data-testid={`me-dev-${i}`}>
                        <td className="px-3 py-1.5 font-medium text-[var(--c-text)]">{d.field}</td>
                        <td className="px-2 py-1.5 font-mono text-[11px] text-[var(--c-green)]">{d.stated}</td>
                        <td className="px-2 py-1.5 font-mono text-[11px] text-[var(--c-muted)]">{d.calculated}</td>
                        <td className={`px-2 py-1.5 font-mono text-[10px] ${SEV[d.severity] || ""}`}>
                          {d.severity === "spec_issue" ? "SPEC ISSUE" : d.severity}
                          {d.delta_pct != null ? ` · ${d.delta_pct > 0 ? "+" : ""}${d.delta_pct}%` : ""}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!!model?.inventory?.length && (
              <div className="overflow-hidden rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)]" data-testid="me-inventory">
                <div className="border-b border-[var(--c-border)] px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-[var(--c-muted)]">
                  Inventory map · {model.rules_applied?.length || 0} rules applied
                </div>
                <table className="w-full text-[12px]">
                  <tbody>
                    {model.inventory.map((r) => (
                      <tr key={r.part} className="border-b border-[var(--c-border)]/50" data-testid={`me-inv-${r.part}`}>
                        <td className="px-3 py-1.5 font-medium text-[var(--c-text)]">{r.label}</td>
                        <td className="w-14 px-2 py-1.5 text-right font-mono text-[11px] tabular-nums">
                          <span className={r.included ? "text-[var(--c-text)]" : "text-[var(--c-red)]"}>{r.count}</span>
                        </td>
                        <td className="px-2 py-1.5 text-[11px] text-[var(--c-muted)]">{r.reason}</td>
                        <td className="w-28 px-2 py-1.5 text-right font-mono text-[9px] text-[var(--c-accent)]">{r.rule || ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <aside className="min-w-0">
            {mode === "project" && showInputs ? (
              <InputsPanel projectId={pid} data={inputs} onChange={setInputs} busy={busy} setBusy={setBusy}
                onGenerated={(m) => { setModel(m); setShowInputs(false); setCompliance(null); runValidation(); }} />
            ) : (
              <>
                <header className="mb-4">
                  <p className="mb-1.5 font-mono text-[10px] font-bold tracking-wider text-[var(--c-accent)]" data-testid="me-eyebrow">{meta.eyebrow}</p>
                  <h1 className="mb-2 text-2xl font-bold leading-tight text-[var(--c-text)]">{meta.title}</h1>
                  <p className="m-0 max-w-[62ch] text-[13.5px] text-[var(--c-muted)]">{meta.lede}</p>
                </header>
                {mode === "project" && (
                  <div className="mb-3 flex rounded-sm border border-[var(--c-border)] p-[2px]">
                    {[["spec", "Specification"], ["compliance", "Compliance"], ["exports", "Exports"]].map(([k, l]) => (
                      <button key={k} data-testid={`me-rail-${k}`} onClick={() => setRail(k)}
                        className={`h-7 flex-1 rounded-sm px-2 text-[11px] ${rail === k
                          ? "bg-[var(--c-accent)] text-white" : "text-[var(--c-muted)] hover:text-[var(--c-text)]"}`}>
                        {l}
                        {k === "compliance" && compliance ? ` · ${compliance.status}` : ""}
                      </button>
                    ))}
                  </div>
                )}
                {mode === "project" && rail === "compliance" ? (
                  <CompliancePanel data={compliance} busy={busy} onRun={runValidation}
                    onSelectPart={pick} selected={selected} />
                ) : mode === "project" && rail === "exports" ? (
                  <ExportsPanel projectId={pid} model={model} compliance={compliance}
                    viewerRef={viewerRef} busy={busy} setBusy={setBusy} />
                ) : (
                  <SpecRail model={model} selected={selected} onSelect={pick} />
                )}
              </>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}
